/**
 * Layer Preset Service
 *
 * Frontend API client for managing layer presets via REST API.
 * Communicates with backend presetsRouter.ts.
 */

import {
  LayerPreset,
  CreatePresetInput,
  UpdatePresetInput,
  PresetFilters,
} from '../types/layerPreset';
import { getBackendUrl, isDevMode } from './configService';

// Get backend URL from runtime config (fail-fast, no fallback)
const getApiBaseUrl = (): string => {
  const url = getBackendUrl();
  if (!url) {
    throw new Error('❌ FATAL: BACKEND_URL not configured in runtime config');
  }
  return url;
};

export class LayerPresetService {
  private get baseUrl(): string {
    return `${getApiBaseUrl()}/api/layer-presets`;
  }

  /**
   * Get auth token from localStorage
   */
  private getAuthToken(): string | null {
    const token = localStorage.getItem('access_token');

    if (!token && isDevMode()) {
      return 'dev-mode-token';
    }

    return token;
  }

  /**
   * Build headers with authentication
   */
  private getHeaders(): HeadersInit {
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    };

    const token = this.getAuthToken();
    // Only add Authorization header if we have a REAL token
    // Don't send fake 'dev-mode-token' - let cookie handle auth instead
    if (token && token !== 'dev-mode-token') {
      headers['Authorization'] = `Bearer ${token}`;
    }

    return headers;
  }

  /**
   * Get all presets for the current user (their presets + global presets)
   */
  async getPresetsForCurrentUser(): Promise<LayerPreset[]> {
    try {
      const response = await fetch(`${this.baseUrl}?include_global=true`, {
        method: 'GET',
        headers: this.getHeaders(),
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch presets: ${response.status}`);
      }

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.message || 'Failed to fetch presets');
      }

      return result.data;
    } catch (error) {
      console.error('Error fetching presets:', error);
      throw error;
    }
  }

  /**
   * Get all global presets
   */
  async getGlobalPresets(): Promise<LayerPreset[]> {
    try {
      const response = await fetch(`${this.baseUrl}/global`, {
        method: 'GET',
        headers: this.getHeaders(),
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch global presets: ${response.status}`);
      }

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.message || 'Failed to fetch global presets');
      }

      return result.data;
    } catch (error) {
      console.error('Error fetching global presets:', error);
      throw error;
    }
  }

  /**
   * Get presets for a specific user
   */
  async getPresetsForUser(userId: string): Promise<LayerPreset[]> {
    try {
      const response = await fetch(`${this.baseUrl}/user/${userId}`, {
        method: 'GET',
        headers: this.getHeaders(),
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch user presets: ${response.status}`);
      }

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.message || 'Failed to fetch user presets');
      }

      return result.data;
    } catch (error) {
      console.error('Error fetching user presets:', error);
      throw error;
    }
  }

  /**
   * Get presets with optional filters
   */
  async getAllPresets(filters?: PresetFilters): Promise<LayerPreset[]> {
    try {
      const params = new URLSearchParams();

      if (filters) {
        if (filters.user_id) params.append('user_id', filters.user_id);
        if (filters.is_global !== undefined) params.append('is_global', String(filters.is_global));
      }

      const url = params.toString() ? `${this.baseUrl}?${params.toString()}` : this.baseUrl;

      const response = await fetch(url, {
        method: 'GET',
        headers: this.getHeaders(),
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch presets: ${response.status}`);
      }

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.message || 'Failed to fetch presets');
      }

      return result.data;
    } catch (error) {
      console.error('Error fetching presets:', error);
      throw error;
    }
  }

  /**
   * Get a single preset by ID
   */
  async getPresetById(id: string): Promise<LayerPreset> {
    try {
      const response = await fetch(`${this.baseUrl}/${id}`, {
        method: 'GET',
        headers: this.getHeaders(),
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch preset: ${response.status}`);
      }

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.message || 'Failed to fetch preset');
      }

      return result.data;
    } catch (error) {
      console.error('Error fetching preset:', error);
      throw error;
    }
  }

  /**
   * Resolve a saved map by its permalink id (#557). Bumps access metrics on the
   * backend. Used when opening /map?permalink=<id>.
   */
  async resolvePermalink(id: string): Promise<LayerPreset> {
    try {
      const response = await fetch(`${this.baseUrl}/${id}/permalink`, {
        method: 'GET',
        headers: this.getHeaders(),
        credentials: 'include',
      });

      if (!response.ok) {
        const errorResult = await response.json().catch(() => ({}));
        throw new Error(errorResult.message || `Failed to resolve permalink: ${response.status}`);
      }

      const result = await response.json();
      if (!result.success) {
        throw new Error(result.message || 'Failed to resolve permalink');
      }
      return result.data;
    } catch (error) {
      console.error('Error resolving permalink:', error);
      throw error;
    }
  }

  /**
   * Create a new preset
   */
  async createPreset(input: CreatePresetInput): Promise<LayerPreset> {
    try {
      const response = await fetch(this.baseUrl, {
        method: 'POST',
        headers: this.getHeaders(),
        credentials: 'include',
        body: JSON.stringify(input),
      });

      if (!response.ok) {
        const errorResult = await response.json();
        throw new Error(errorResult.message || `Failed to create preset: ${response.status}`);
      }

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.message || 'Failed to create preset');
      }

      return result.data;
    } catch (error) {
      console.error('Error creating preset:', error);
      throw error;
    }
  }

  /**
   * Update an existing preset
   */
  async updatePreset(id: string, input: UpdatePresetInput): Promise<LayerPreset> {
    try {
      const response = await fetch(`${this.baseUrl}/${id}`, {
        method: 'PUT',
        headers: this.getHeaders(),
        credentials: 'include',
        body: JSON.stringify(input),
      });

      if (!response.ok) {
        const errorResult = await response.json();
        throw new Error(errorResult.message || `Failed to update preset: ${response.status}`);
      }

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.message || 'Failed to update preset');
      }

      return result.data;
    } catch (error) {
      console.error('Error updating preset:', error);
      throw error;
    }
  }

  /**
   * Delete a preset
   */
  async deletePreset(id: string): Promise<boolean> {
    try {
      const response = await fetch(`${this.baseUrl}/${id}`, {
        method: 'DELETE',
        headers: this.getHeaders(),
        credentials: 'include',
      });

      if (!response.ok) {
        const errorResult = await response.json();
        throw new Error(errorResult.message || `Failed to delete preset: ${response.status}`);
      }

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.message || 'Failed to delete preset');
      }

      return true;
    } catch (error) {
      console.error('Error deleting preset:', error);
      throw error;
    }
  }
}

// Export a singleton instance for convenience
export const layerPresetService = new LayerPresetService();
